import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isActionOf } from "typesafe-actions";

import { translate } from "app/filters/i18nFilter";
import { managementSignalsApiQueryKeys } from "app/pages/sales-intelligence/modules/management/sub-modules/signals-api/query/key-factory/queryKeyFactory";
import { signalsRestApiQueryKeys } from "app/pages/sales-intelligence/modules/signals-rest-api/query/key-factory/queryKeyFactory";
import DeleteSignalModalTracked from "app/pages/sales-intelligence/pages/opportunity-list/signals/containers/DeleteSignalModal/DeleteSignalModalTracked";
import SignalSource from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalSource";
import type { RootState } from "single-spa/store/types";

import type { DeleteSignalModalProps } from "../../../../../sub-modules/signals/components/modals/DeleteSignalModal/DeleteSignalModal";
import type SignalFilters from "../../../../../sub-modules/signals/models/core/SignalFilters";
import type UISignal from "../../../../../sub-modules/signals/models/view/UISignal";
import {
    appliedSignalsChangedAction,
    deleteSignalsAsync,
} from "../../../../../sub-modules/signals/store/action-creators";
import { deleteSignalThunk } from "../../../../../sub-modules/signals/store/effects";
import {
    selectAppliedSignalsIds,
    selectSignalIsBeingDeleted,
    selectSignalsSource,
} from "../../../../../sub-modules/signals/store/selectors";

type DeleteSignalModalContainerProps = Pick<DeleteSignalModalProps, "signal" | "onClose"> & {
    onDeleteSuccess?(): void;
};

const DeleteSignalModalContainer = (props: DeleteSignalModalContainerProps) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const [deleteError, setDeleteError] = useState<string | undefined>();

    const appliedSignalsIds = useSelector(selectAppliedSignalsIds);
    const signalSource = useSelector(selectSignalsSource);
    const signalId = props.signal?.id ?? null;
    const isDeleteInProgress = useSelector((state: RootState) => {
        if (props.signal === null) {
            return false;
        }

        return selectSignalIsBeingDeleted(state, props.signal.id);
    });

    useEffect(() => {
        setDeleteError(undefined);
    }, [signalId]);

    const handleClose = useCallback(() => {
        setDeleteError(undefined);
        props.onClose();
    }, [props]);

    async function handleDeleteSubmit(signal: Readonly<UISignal<SignalFilters>>) {
        setDeleteError(undefined);

        const resultAction = await dispatch(deleteSignalThunk(signal.id));

        if (isActionOf(deleteSignalsAsync.success, resultAction)) {
            if (signalSource === SignalSource.Api) {
                void queryClient.invalidateQueries({ queryKey: managementSignalsApiQueryKeys.getStatus() });
            }

            if (signalSource === SignalSource.RestApi) {
                void queryClient.invalidateQueries({ queryKey: signalsRestApiQueryKeys.getConfiguredStatus() });
            }
            if (appliedSignalsIds.includes(signal.id)) {
                dispatch(appliedSignalsChangedAction([]));
            }

            props.onDeleteSuccess?.();
            handleClose();

            return;
        }

        if (isActionOf(deleteSignalsAsync.failure, resultAction)) {
            const prefix = translate("si.signals.delete-signal.error");
            const serverMessage = resultAction.payload.error;

            setDeleteError(serverMessage ? `${prefix} ${serverMessage}` : prefix);
        }
    }

    return (
        <DeleteSignalModalTracked
            {...props}
            errorMessage={deleteError}
            isDeleteInProgress={isDeleteInProgress}
            isOpen={props.signal !== null}
            onClose={handleClose}
            onSubmitClick={handleDeleteSubmit}
        />
    );
};

export default DeleteSignalModalContainer;
